/**
 * Lykke voice sessions on the app side (client launch, Nov 2026): the contract the cowork API keeps for every voice
 * call (cowork engine/voiceSessions.ts, routes/voiceOpen.ts, routes/calls.ts).
 *
 * 1. The app creates its WebRTC offer and sends it as `sdp` to the mint route (present-session, strategize-secret,
 *    call-session, ears-secret, the kickoff and order realtime-secret, live-session). The server connects the call
 *    and answers OpenAI's `sdp`, plus `voiceSessionId`, `maxMinutes` and `endsAt`. (An older answer may still carry a
 *    `clientSecret`: the app then connects itself and reports the call id on its first heartbeat.)
 * 2. While the call runs: POST /v1/calls/voice/:id/alive about every 45 s. The server hangs up a call whose
 *    heartbeat stopped for 2 minutes. 409 voice_session_over / 404 means the server already ended it: stop.
 * 3. The call ends: POST /v1/calls/voice/:id/end (hang-up, errors, the page going away: fetch with keepalive), so the
 *    unused minutes go back to the team.
 * 4. At `endsAt` the app stops the call itself with a calm line (the server cuts it 20 s later).
 *
 * Mint failures are the usual answers: 429 cap_reached (the shared cap message and toast), 403 terms_required (the
 * terms modal reopens), 403 feature_closed / clients_paused, 409 voice_connect_required. `voiceErrorText` turns
 * them into one plain line for the call UI.
 */
import { API_BASE_URL, authHeaders } from '@/cowork/omniad/api/coworkApi';
import { accessClosedOf, capReachedOf, classifyApiError } from '@/cowork/launch/apiSignals';
import { capReachedMessage } from '@/cowork/launch/caps';
import { closedMessage } from '@/cowork/launch/studioErrors';

/** Heartbeat cadence: well inside the server's 120 s timeout. */
export const VOICE_HEARTBEAT_MS = 45_000;

/** Said when a call reaches the minutes it was given (`endsAt`). */
export const VOICE_TIME_UP = 'This call has reached its time limit. Start a new call to keep talking.';
/** Said when the server already ended the session (it timed out, or another tab ended it). */
export const VOICE_SESSION_OVER = 'This call has ended.';
export const VOICE_TERMS_REQUIRED = 'Accept the AI terms to talk to Lykke.';
export const VOICE_UPDATE_APP = 'Reload the page to talk to Lykke.';

/** The session fields every metered mint route adds to its answer. */
export interface VoiceSessionFields { voiceSessionId?: string; maxMinutes?: number; endsAt?: string }

/** A mint answer: the server-connected call (`sdp`), or a client secret for an app-connected one. */
export interface VoiceMintAnswer extends VoiceSessionFields { sdp?: string; clientSecret?: string; expiresAt?: number }

/** Sends the app's WebRTC offer to a mint route and returns its answer. */
export type VoiceMint<T extends VoiceMintAnswer = VoiceMintAnswer> = (offerSdp: string) => Promise<T>;

export type VoiceOverReason = 'time_up' | 'session_over';

/** The calm line for a call the session stopped. */
export function voiceOverText(reason: VoiceOverReason): string {
  return reason === 'time_up' ? VOICE_TIME_UP : VOICE_SESSION_OVER;
}

function recordOf(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/**
 * The line for a mint the API refused on purpose: the monthly cap (429 cap_reached), the terms (403 terms_required),
 * a closed or paused studio (403), an app too old for the server (409 voice_connect_required). Null for anything else.
 */
export function voiceRefusalText(err: unknown): string | null {
  const cap = capReachedOf(err);
  if (cap) return capReachedMessage(cap, 'voice');
  const e = recordOf(err);
  const status = e && typeof e.status === 'number' ? e.status : null;
  const body = recordOf(e?.body);
  if (status === 403 && classifyApiError(403, body)?.kind === 'terms_required') return VOICE_TERMS_REQUIRED;
  const closed = accessClosedOf(err);
  if (closed) return closedMessage(closed);
  if (body && (body.code === 'voice_connect_required' || body.error === 'voice_connect_required')) return VOICE_UPDATE_APP;
  return null;
}

/** One plain line for a failed call start: a refusal's line (voiceRefusalText), else the error's own message. */
export function voiceErrorText(err: unknown, otherwise = 'The call is not available right now.'): string {
  return voiceRefusalText(err) ?? (err instanceof Error && err.message ? err.message : otherwise);
}

/** The OpenAI Realtime call id from the `Location` of POST /v1/realtime/calls (".../calls/{id}"), else null. */
export function callIdFromLocation(location: string | null | undefined): string | null {
  if (!location) return null;
  const m = /\/calls\/([A-Za-z0-9_-]{6,200})\/?(?:[?#].*)?$/.exec(location.trim());
  return m ? m[1] : null;
}

const SESSION_ID = /^vs_[0-9a-f-]{36}$/;

/** A device clock this far from the server's is corrected for (see VoiceKeeperOptions.maxMinutes). */
const SKEW_TOLERANCE_MS = 60_000;

/** The session id of a mint answer, when it is one the server made (older APIs answer without it). */
export function voiceSessionIdOf(answer: unknown): string | null {
  const id = recordOf(answer)?.voiceSessionId;
  return typeof id === 'string' && SESSION_ID.test(id) ? id : null;
}

const voicePath = (id: string, action: 'alive' | 'end') => `${API_BASE_URL}/v1/calls/voice/${encodeURIComponent(id)}/${action}`;

/** POST /v1/calls/voice/:id/end with fetch keepalive (it survives the page going away). Never throws. */
export function sendVoiceEnd(sessionId: string, fetchImpl: typeof fetch = (...a) => fetch(...a)): void {
  try {
    void fetchImpl(voicePath(sessionId, 'end'), { method: 'POST', headers: authHeaders(), body: '{}', keepalive: true }).catch(() => { /* the server sweep ends it */ });
  } catch { /* the server sweep ends it */ }
}

/** What the keeper listens on for the page going away (the window). */
export interface PageHideTarget {
  addEventListener(type: 'pagehide', listener: () => void): void;
  removeEventListener(type: 'pagehide', listener: () => void): void;
}

export interface VoiceKeeperOptions {
  sessionId: string;
  /** When the server stops the call (ISO). The app stops it here first, with a calm line. */
  endsAt?: string | null;
  /**
   * The minutes the mint granted (`maxMinutes`). With it, a device clock that is off by more than a minute does not
   * move the stop: the call stops `maxMinutes` after the mint answered on this device, not at the server's clock time.
   */
  maxMinutes?: number | null;
  /** The call stopped on the server side (`session_over`) or reached `endsAt` (`time_up`): end the call now. */
  onOver: (reason: VoiceOverReason) => void;
  heartbeatMs?: number;
  fetchImpl?: typeof fetch;
  /** Where to listen for the page going away (pagehide); `null` for none. Defaults to `window`. */
  target?: PageHideTarget | null;
  now?: () => number;
}

/**
 * Keeps one voice session honest: heartbeats while the call runs, stops it at `endsAt`, and always tells the server
 * when it ends (hang-up, error, the page going away). Create it as soon as the mint answers (so even a call that
 * fails to connect gives its minutes back), `start()` once the call is up, `end()` exactly when the call ends
 * (idempotent; also after `onOver`).
 */
export class VoiceSessionKeeper {
  readonly sessionId: string;
  private endsAt: number | null;
  /** Server clock minus this device's clock, when they differ by more than a minute (else 0). */
  private skew = 0;
  private callId: string | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private timeUp: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private ended = false;
  private readonly opts: VoiceKeeperOptions;
  private readonly fetchImpl: typeof fetch;
  private readonly target: PageHideTarget | null;
  private readonly onPageHide = () => this.end();

  constructor(opts: VoiceKeeperOptions) {
    this.opts = opts;
    this.sessionId = opts.sessionId;
    const at = opts.endsAt ? Date.parse(opts.endsAt) : NaN;
    this.endsAt = Number.isNaN(at) ? null : at;
    const granted = typeof opts.maxMinutes === 'number' && Number.isFinite(opts.maxMinutes) && opts.maxMinutes > 0 ? opts.maxMinutes * 60_000 : null;
    if (this.endsAt !== null && granted !== null) {
      // The server sets endsAt = its own now + maxMinutes. A device clock far off would stop the call at once (clock
      // ahead) or late (clock behind): measure the difference once and read every endsAt in this device's time.
      const skew = this.endsAt - (this.nowMs() + granted);
      if (Math.abs(skew) > SKEW_TOLERANCE_MS) { this.skew = skew; this.endsAt -= skew; }
    }
    this.fetchImpl = opts.fetchImpl ?? ((...a) => fetch(...a));
    this.target = opts.target === undefined ? (typeof window !== 'undefined' ? window : null) : opts.target;
    // The page can go away while the call is still connecting: the session must end with it.
    this.target?.addEventListener('pagehide', this.onPageHide);
  }

  get active(): boolean { return !this.ended; }

  private nowMs(): number { return this.opts.now ? this.opts.now() : Date.now(); }

  /**
   * The call is up: heartbeat every ~45 s and stop at `endsAt`. `callId` only when the app connected the call itself
   * (an older answer with a client secret): it goes with the first heartbeat, sent at once, so the server can end it.
   */
  start(callId?: string | null): void {
    if (this.ended || this.started) return;
    this.started = true;
    this.callId = callId || null;
    this.heartbeat = setInterval(() => { void this.beat(); }, this.opts.heartbeatMs ?? VOICE_HEARTBEAT_MS);
    if (this.callId) void this.beat();
    this.scheduleTimeUp();
  }

  /** The call ended (hang-up, error, page hide): stop the timers and tell the server, once. */
  end(): void {
    if (this.ended) return;
    this.ended = true;
    this.clearTimers();
    this.target?.removeEventListener('pagehide', this.onPageHide);
    sendVoiceEnd(this.sessionId, this.fetchImpl);
  }

  private clearTimers() {
    if (this.heartbeat) { clearInterval(this.heartbeat); this.heartbeat = null; }
    if (this.timeUp) { clearTimeout(this.timeUp); this.timeUp = null; }
  }

  private scheduleTimeUp() {
    if (this.timeUp) { clearTimeout(this.timeUp); this.timeUp = null; }
    if (this.endsAt === null || this.ended) return;
    this.timeUp = setTimeout(() => this.over('time_up'), Math.max(0, this.endsAt - this.nowMs()));
  }

  private over(reason: VoiceOverReason) {
    if (this.ended) return;
    this.clearTimers();
    try { this.opts.onOver(reason); } finally { this.end(); }
  }

  private async beat(): Promise<void> {
    if (this.ended) return;
    const callId = this.callId;
    let res: Response;
    try {
      res = await this.fetchImpl(voicePath(this.sessionId, 'alive'), { method: 'POST', headers: authHeaders(), body: JSON.stringify(callId ? { callId } : {}) });
    } catch { return; /* a blip: the next beat tries again (the server allows 2 minutes) */ }
    if (this.ended) return;
    const body = recordOf(await res.json().catch(() => null)) ?? {};
    if (res.ok) {
      if (callId) this.callId = null; // reported once
      const at = typeof body.endsAt === 'string' ? Date.parse(body.endsAt) - this.skew : NaN;
      if (!Number.isNaN(at) && at !== this.endsAt) { this.endsAt = at; this.scheduleTimeUp(); }
      return;
    }
    // The server ended the session already (swept, ended elsewhere) or does not know it: stop the call.
    if (res.status === 404 || (res.status === 409 && body.code === 'voice_session_over')) { this.over('session_over'); return; }
    // A call id it will not take (bad, or claimed by another session): keep the heartbeat without it.
    if (body.code === 'call_taken' || body.code === 'bad_call_id') this.callId = null;
  }
}

/**
 * The keeper for a mint answer, or null when the answer has no voice session (an older API, or a route that is not
 * metered). `onOver` ends the call.
 */
export function keeperFor(answer: unknown, onOver: (reason: VoiceOverReason) => void, opts: Partial<Omit<VoiceKeeperOptions, 'sessionId' | 'onOver'>> = {}): VoiceSessionKeeper | null {
  const id = voiceSessionIdOf(answer);
  if (!id) return null;
  const endsAt = recordOf(answer)?.endsAt;
  const maxMinutes = recordOf(answer)?.maxMinutes;
  return new VoiceSessionKeeper({ ...opts, sessionId: id, endsAt: typeof endsAt === 'string' ? endsAt : null, maxMinutes: typeof maxMinutes === 'number' ? maxMinutes : null, onOver });
}
